import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Heart, MessageCircleHeart, CheckCircle2, Users2, Clock } from "lucide-react";
import { toast } from "sonner";
import ThankDialog from "./ThankDialog";

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return "just now";
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function GifterItemRow({ gifterId, item, onThank }) {
  return (
    <div className="flex items-center gap-3" data-testid={`gifter-${gifterId}-item-${item.id}`}>
      <div className="h-10 w-10 rounded-lg overflow-hidden bg-cream-surface shrink-0">
        {item.image_url && <img src={item.image_url} alt={item.product_name} className="h-full w-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-manrope text-sm text-ink truncate">{item.product_name}</p>
        <p className="font-manrope text-xs text-ink-soft">
          {item.status === "purchased" ? "Gifted" : "Claimed"}
          {item.thanked ? " · thanked ✓" : " · not yet thanked"}
        </p>
      </div>
      {!item.thanked && (
        <button
          onClick={onThank}
          className="btn-ghost text-xs inline-flex items-center gap-1 whitespace-nowrap"
          data-testid={`gifter-thank-btn-${item.id}`}
        >
          <MessageCircleHeart className="h-3.5 w-3.5" /> Thank
        </button>
      )}
    </div>
  );
}

function GifterCard({ gifter, onThank }) {
  return (
    <div className="card-soft p-6" data-testid={`gifter-${gifter.user_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-outfit text-lg font-medium text-ink">{gifter.display_name || "Anonymous"}</h3>
          <div className="mt-1 flex flex-wrap gap-2 text-xs">
            <span className="tag-pill" data-testid={`gifter-claimed-${gifter.user_id}`}>
              {gifter.claimed_count} claimed
            </span>
            <span className="tag-pill !bg-[#F3E6FA] !text-[#6B2186]" data-testid={`gifter-purchased-${gifter.user_id}`}>
              {gifter.purchased_count} gifted
            </span>
            {gifter.pending_thanks > 0 ? (
              <span className="tag-pill !bg-[#FFF1D6] !text-[#8A5A00]" data-testid={`gifter-pending-${gifter.user_id}`}>
                {gifter.pending_thanks} thank-yous pending
              </span>
            ) : (
              <span className="tag-pill !bg-[#E9F6EE] !text-[#2D6A4F] inline-flex items-center gap-1" data-testid={`gifter-caughtup-${gifter.user_id}`}>
                <CheckCircle2 className="h-3 w-3" /> All thanked
              </span>
            )}
          </div>
        </div>
        {gifter.last_activity && (
          <span className="font-manrope text-xs text-ink-soft inline-flex items-center gap-1 shrink-0">
            <Clock className="h-3 w-3" /> {relativeTime(gifter.last_activity)}
          </span>
        )}
      </div>
      <div className="mt-4 border-t border-[#EBE2E8] pt-3 space-y-2">
        {gifter.items.map((it) => (
          <GifterItemRow
            key={it.id}
            gifterId={gifter.user_id}
            item={it}
            onThank={() => onThank({ gifter, item: it })}
          />
        ))}
      </div>
    </div>
  );
}

function RosterEmptyState() {
  return (
    <div className="mt-6 card-soft p-8 text-center" data-testid="gifters-empty">
      <Heart className="h-8 w-8 text-brand mx-auto mb-3 opacity-40" />
      <p className="font-manrope text-sm text-ink-soft">
        No one has claimed an item yet. Once someone does, they'll show up here.
      </p>
    </div>
  );
}

export default function GiftersRoster() {
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thanking, setThanking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/registry/me/gifters");
      setRoster(data);
    } catch (e) {
      toast.error("Couldn't load gifters.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-12" data-testid="gifters-roster-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-outfit text-2xl font-medium text-ink inline-flex items-center gap-2">
            <Users2 className="h-5 w-5 text-brand" /> Your gifters
          </h2>
          <p className="font-manrope text-sm text-ink-soft mt-1">
            Everyone who's claimed or gifted something — and who you still owe a thank-you to.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="mt-6 font-manrope text-ink-soft animate-soft-pulse">Loading gifters…</p>
      ) : roster.length === 0 ? (
        <RosterEmptyState />
      ) : (
        <div className="mt-6 grid md:grid-cols-2 gap-5">
          {roster.map((g) => (
            <GifterCard key={g.user_id} gifter={g} onThank={setThanking} />
          ))}
        </div>
      )}

      <ThankDialog entry={thanking} onClose={() => setThanking(null)} onSent={load} />
    </div>
  );
}
